import React from 'react';
import { motion } from 'framer-motion';
import { Lock, Sparkles, ArrowRight } from 'lucide-react';
import { Button } from '../ui/button';
import { useAuth } from '../../contexts/AuthContext';

const DEMO_EMAILS = [
    'admin@homly.pro',
    'agent@homly.pro',
    'agent2@homly.pro',
];

export function isDemoAccount(email) {
    return DEMO_EMAILS.includes(email?.toLowerCase());
}

/**
 * Retourne true si on est en environnement de preview (dev/test).
 * En production (emergent.host, homly.pro, etc.), retourne false.
 */
export function isPreviewMode() {
    const hostname = window.location.hostname;
    return hostname.includes('preview.emergentagent.com') || hostname === 'localhost' || hostname === '127.0.0.1';
}

export function BetaLockOverlay({ children, featureName = 'Cette fonctionnalité', productionOnly = false }) {
    const { user } = useAuth();

    // productionOnly: locked in production regardless of account type
    const inProduction = !isPreviewMode();
    const shouldLock = productionOnly ? inProduction : !isDemoAccount(user?.email);

    if (!shouldLock) {
        return <>{children}</>;
    }

    return (
        <div className="relative min-h-[70vh]">
            {/* Lock overlay - rendered FIRST as standalone */}
            <div className="absolute inset-0 flex items-center justify-center z-20">
                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                    className="text-center max-w-sm mx-auto px-6"
                >
                    {/* Lock icon with glow */}
                    <div className="relative mx-auto mb-6 w-20 h-20">
                        <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-[#7C3AED] to-[#F59E0B] opacity-20 blur-xl" />
                        <div className="relative w-20 h-20 rounded-2xl bg-gradient-to-br from-[#7C3AED]/20 to-[#F59E0B]/20 border border-[#7C3AED]/30 flex items-center justify-center backdrop-blur-sm">
                            <Lock className="w-8 h-8 text-[#F59E0B]" />
                        </div>
                    </div>

                    {/* Text */}
                    <h3 className="text-lg font-bold text-foreground mb-2">
                        Bientôt disponible
                    </h3>
                    <p className="text-sm text-muted-foreground mb-1">
                        {featureName} sera accessible après la phase bêta.
                    </p>
                    <p className="text-xs text-muted-foreground/60 mb-6">
                        Nous peaufinons cette section pour vous offrir la meilleure expérience possible.
                    </p>

                    {/* Badge */}
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-[#7C3AED]/10 to-[#F59E0B]/10 border border-[#7C3AED]/20">
                        <Sparkles className="w-3.5 h-3.5 text-[#F59E0B]" />
                        <span className="text-xs font-medium text-foreground">En cours de développement</span>
                    </div>
                </motion.div>
            </div>

            {/* Blurred content behind (purely decorative) */}
            <div className="blur-[6px] opacity-15 pointer-events-none select-none" aria-hidden="true">
                {children}
            </div>
        </div>
    );
}

export default BetaLockOverlay;
